import { PriceType } from './equipment';
import { ServiceOrderDetails } from './service-order';
import {
  OffRentOrderData,
  ScheduleOffRentOrderDetails,
  OffRentOrderUpdateFormFieldKey,
  OffRentOrderUpdateFormFieldType,
  CancelOffRentOrderDetails,
} from './offrent-types';
import {
  AddPurchaseOrderFromModal,
  PurchaseOrderDetails,
  PurchaseOrderUpdateFormFieldKey,
  PurchaseOrderUpdateFormFieldType,
  UpdatePurchaseOrderFromModal,
} from './purchase-order';
import {
  RentalProtectionType,
  DeliveryOptionType,
  DropOffOptionType,
  ReceiverOptionType,
} from './checkout-common';

export interface Order {
  type: 'ORDER';
  guest_user_request: boolean;
  rental_protection_plan: RentalProtectionType;
  shift_id: string;
  shift_plan_name: string;
  shift_plan_multiplier: number;
  shift_plan_description: string;
  sales_staff_email?: string;
  sales_staff_information?: string;
  delivery_option: DeliveryOptionType;
  dropoff_option: DropOffOptionType;
  receiver_option: ReceiverOptionType;
  receiver_contact_name: string;
  receiver_contact_phone: string;
  delivery_instructions: string;
  branch_id: string;
  latitude: string;
  longitude: string;
  get_directions_link: string;
  city: string;
  state: string;
  state_name: string;
  rpp_id?: number;
  rpp_name?: string;
  rpp_cost: number;
  rpp_percentage: number;
  equipment_charges: number;
  delivery_fee: number;
  dropoff_fee: number;
  total_delivery_fee: number;
  taxes: number;
  is_tax_exempt: boolean;
  rental_subtotal: number;
  order_total: number;
  timezone: string;
  user: {
    email_address: string;
    mobile_number: string;
    first_name: string;
    last_name: string;
    company_name: string | null;
  };
  line_items: OrderLineItem[];
  jobsite_id?: number | null;
  jobsite_name?: string;
  jobsite_address: {
    address_line1: string;
    city: string;
    state: string;
    zipcode: string;
  };
  jobsite_latitude: string | null;
  jobsite_longitude: string | null;
  jobsite_abbreviated_state: string | null;
  po_number: string | null;
}

export interface OrderLineItem {
  equipment_class_id: string;
  equipment_name: string;
  equipment_image_src: string;
  slug: string;
  quantity: number;
  start_date: number;
  end_date: number;
  rental_pricing: number;
  rental_pricing_type?: PriceType;
  day_rate: number;
  week_rate: number;
  four_week_rate: number;
  rate_type_name: string;
}

export interface OrderResponse extends Order {
  id: string;
}

export enum OrderType {
  ORDER = 'ORDER',
  QUOTE = 'QUOTE',
}

export interface ShowEquipmentItem {
  equipment: string;
  quantity: number;
}

export interface ShowOrderItem {
  orderId: string;
  orderPlaced: number;
  jobsite: string;
  tenureStartDate: number;
  tenureEndDate: number;
  equipments: ShowEquipmentItem[];
  statusId: number | string;
}

export enum ORDER_HISTORY_TYPE {
  ORDERS = 'ORDERS',
}

export enum RENTAL_TYPE {
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
  PAST = 'PAST',
}

export enum ORDERS_VIEW_STATE {
  HOME = 'HOME',
  ORDERS = 'ORDERS',
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
  PAST = 'PAST',
}

export interface ORDER_TABLE_TAB {
  labelId: string;
  type: ORDER_HISTORY_TYPE;
  handleClick: () => void;
}

export interface RENTAL_TABLE_TAB {
  labelId: string;
  type: RENTAL_TYPE;
  handleClick: () => void;
}

export interface ShowActiveRentalItem {
  imageUrl: string | null;
  equipment: {
    className: string;
    classId: number | null;
    categoryName: string;
    categoryId: number;
    makeName: string;
    makeId: number;
    slug: string | null;
    description: string;
  };
  assetId: number | null;
  latestPartAssignment?: {
    partName: string;
    partNumber: number | null;
  };
  model: string;
  locationId: number;
  startDate: string;
  endDate: string;
  jobsiteName: string;
  jobsiteAddress: string;
  rentalId: number;
  orderId: number;
  off_rent_date_requested: string | null | undefined;
  purchase_order: {
    name: string;
    purchase_order_id: number | null;
    purchase_order_start_date: string;
    purchase_order_end_date: string;
    budget_amount: number | null;
  };
  service_location_city: string;
  isAssetPresent: boolean;
  market_id: string;
  rental_type_id?: number;
}

export enum SORT_BY {
  START_DATE = 'start_date',
  END_DATE = 'end_date',
}

export enum ORDER_BY {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export interface RefreshRentalsPayload {
  refresh: boolean;
  sortBy: SORT_BY;
  orderBy: ORDER_BY;
}

export interface UpdatePurchaseOrder {
  startDate?: string;
  endDate?: string;
  budget_amount?: number;
  tenureStartDate: number;
  tenureEndDate: number;
  equipments: ShowEquipmentItem[];
}

export enum InputFieldComponentType {
  TEXTFIELD = 'TEXTFIELD',
  SELECT = 'SELECT',
  EXPANDABLE_FORM = 'EXPANDABLE_FORM',
  ADDRESS = 'ADDRESS',
  DATE = 'DATE',
  TEXTAREA = 'TEXTAREA',
}

export type UpdateFormFieldType =
  | PurchaseOrderUpdateFormFieldType
  | OffRentOrderUpdateFormFieldType;

export type UpdateFormFieldKey = PurchaseOrderUpdateFormFieldKey | OffRentOrderUpdateFormFieldKey;

export interface FieldDetailNormal {
  fieldType: UpdateFormFieldType;
  fieldKey: UpdateFormFieldKey;
  fieldLabelId: string;
  fieldSecondaryLabelId?: string;
  inputComponentType: InputFieldComponentType;
  inputType: React.HTMLInputTypeAttribute | undefined;
  inputMode:
    | 'search'
    | 'text'
    | 'none'
    | 'email'
    | 'tel'
    | 'url'
    | 'numeric'
    | 'decimal'
    | undefined;
  selectOptions?: {
    value: string;
    label: string;
  }[];
  defaultSelectOption?: string;
  required: boolean;
  visible: boolean;
  disabled: boolean;
  onSubLabelClick?: () => void;
  adornment?: string;
  placeholderId?: string;
}

export type FieldDetail = FieldDetailNormal;

export type ModalDataType =
  | PurchaseOrderDetails
  | ServiceOrderDetails
  | ScheduleOffRentOrderDetails
  | CancelOffRentOrderDetails;

export enum OrdersModalVisibility {
  NONE = 'NONE',
  CENTERED_MODAL = 'CENTERED_MODAL',
  BOTTOM_SHEET = 'BOTTOM_SHEET',
}

export enum OrderDisplayModalType {
  CENTERED_MODAL = 'CENTERED_MODAL',
  BOTTOM_SHEET = 'BOTTOM_SHEET',
}

export enum OrdersModalType {
  PURCHASE_ORDER_MODAL = 'PURCHASE_ORDER',
  SERVICE_ORDER_MODAL = 'SERVICE_ORDER',
  SCHEDULE_OFFRENT_ORDER_MODAL = 'SCHEDULE_OFFRENT_ORDER',
  CANCEL_OFFRENT_ORDER_MODAL = 'CANCEL_OFFRENT_ORDER_MODAL',
  NONE = 'NONE',
}

export type ModalDataUpdateType =
  | UpdatePurchaseOrderFromModal
  | AddPurchaseOrderFromModal
  | OffRentOrderData;

export interface ActiveRentalActions {
  key: string;
  orderModalType: OrdersModalType;
  updateDataHandler: (rentalId: number, orderData: ModalDataUpdateType) => void;
  actionHandler?: (...args: string[]) => void;
}
